import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Clock, Loader2, Truck, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  shipped: "bg-primary/10 text-primary",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "N/A";

/**
 * Landing page for ops users: order counts by status and the newest orders. Uses only the
 * order list endpoint (its `stats` block), which is the one dashboard source ops may read -
 * no revenue, resellers, products or requests.
 */
export default function OpsDashboard() {
  const [stats, setStats] = useState({ total: 0, pending: 0, processing: 0, shipped: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { params: { page: 1, limit: 10 } });
      setStats(data.stats || {});
      setRecentOrders(data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const tiles = [
    { label: "Total Orders", value: stats.total || 0, icon: ShoppingCart, gradient: "from-purple-500 to-purple-600" },
    { label: "Pending", value: stats.pending || 0, icon: Clock, gradient: "from-amber-500 to-amber-600" },
    { label: "Processing", value: stats.processing || 0, icon: Loader2, gradient: "from-sky-500 to-sky-600" },
    { label: "Shipped", value: stats.shipped || 0, icon: Truck, gradient: "from-emerald-500 to-emerald-600" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ops-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm">Orders at a glance.</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((tile) => (
          <Link
            key={tile.label}
            to="/admin/orders"
            className={`bg-gradient-to-br ${tile.gradient} rounded-2xl p-4 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <tile.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl lg:text-3xl font-extrabold leading-tight">{tile.value}</p>
                <p className="text-sm text-white/80 font-medium truncate">{tile.label}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Recent Orders</h3>
          <Link to="/admin/orders" className="text-xs text-primary hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="p-8 text-center">
            <ShoppingCart className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No orders yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {recentOrders.map((order) => (
              <Link
                key={order.order_id}
                to="/admin/orders"
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {order.order_number ? `#${order.order_number}` : order.order_id?.slice(0, 12)}
                    </p>
                    <p className="text-[0.625rem] text-slate-400">
                      {order.store_name || order.customer_name || order.customer_email || "Guest"} • {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-[0.625rem] font-bold px-2 py-0.5 rounded-md ${statusColors[order.status] || "bg-slate-100 text-slate-600"}`}>
                    {order.status?.toUpperCase()}
                  </span>
                  <span className="text-sm font-bold text-slate-900">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
